class MiniCart extends HTMLElement {
  constructor() {
    super();
    this.loaded = false;
  }

  connectedCallback() {
    this.header = document.querySelector('sticky-header');
    this.drawer = document.querySelector('cart-drawer');

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(this.handleIntersection.bind(this)).observe(this);
    } else {
      this.loadMiniCart();
    }
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;

    observer.unobserve(this);
    this.loadMiniCart();
  }

  loadMiniCart() {
    if (this.loaded) return;

    const miniCartTarget = document.getElementById('mini-cart');

    if (!miniCartTarget) {
      console.warn('[MiniCart] #mini-cart was not found.');
      return;
    }

    if (!this.dataset.url) {
      console.warn('[MiniCart] data-url is missing on <mini-cart>.');
      return;
    }

    fetch(this.dataset.url)
      .then(response => response.text())
      .then(html => {
        const sectionHTML = this.getSectionInnerHTML(html, '.shopify-section');

        if (!sectionHTML) {
          console.warn('[MiniCart] Could not find .shopify-section in fetched mini-cart HTML.');
          return;
        }

        miniCartTarget.innerHTML = sectionHTML;
        this.loaded = true;

        document.dispatchEvent(new CustomEvent('cartdrawer:loaded'));
      })
      .catch(error => {
        console.error('[MiniCart] Failed to load mini cart:', error);
      });
  }

  open() {
    this.drawer = this.drawer || document.querySelector('cart-drawer');

    if (!this.drawer) {
      console.warn('[MiniCart] cart-drawer element was not found.');
      return;
    }

    const detailsElement =
      this.drawer.querySelector('details') ||
      this.drawer.closest('details') ||
      document.querySelector('details.cart-drawer-container') ||
      document.querySelector('.cart-drawer-container details');

    if (detailsElement && detailsElement.hasAttribute('open')) {
      return;
    }

    const summaryElement = detailsElement ? detailsElement.querySelector('summary') : null;

    if (typeof this.drawer.openMenuDrawer === 'function') {
      try {
        this.drawer.openMenuDrawer(summaryElement);
      } catch (error) {
        try {
          this.drawer.openMenuDrawer();
        } catch (fallbackError) {
          console.error('[MiniCart] openMenuDrawer failed:', fallbackError);
          this.forceOpen(detailsElement);
        }
      }
    } else {
      this.forceOpen(detailsElement);
    }

    document.dispatchEvent(new CustomEvent('cartdrawer:opened'));
  }

  forceOpen(detailsElement) {
    if (!detailsElement) {
      console.warn('[MiniCart] Could not force open cart because no details element was found.');
      return;
    }

    detailsElement.setAttribute('open', '');

    requestAnimationFrame(() => {
      detailsElement.classList.add('menu-opening');
      document.documentElement.classList.add('mini-cart--open');
      document.body.classList.add('mini-cart--open');
    });
  }

  renderContents(parsedState) {
    this.productId = parsedState && parsedState.id ? parsedState.id : null;

    if (parsedState && parsedState.sections) {
      this.getSectionsToRender().forEach(section => {
        const target = document.getElementById(section.id);
        const sectionHTML = parsedState.sections[section.id];

        if (target && sectionHTML) {
          const innerHTML = this.getSectionInnerHTML(sectionHTML, section.selector);

          if (innerHTML) {
            target.innerHTML = innerHTML;
          }
        }
      });

      this.open();
      return;
    }

    this.refreshCartSections().then(() => {
      this.open();
    });
  }

  refreshCartSections() {
    const sections = this.getSectionsToRender()
      .map(section => section.section)
      .join(',');

    const url = `${window.location.pathname}?sections=${sections}`;

    return fetch(url)
      .then(response => response.json())
      .then(parsedSections => {
        this.getSectionsToRender().forEach(section => {
          const target = document.getElementById(section.id);
          const sectionHTML = parsedSections[section.section] || parsedSections[section.id];

          if (target && sectionHTML) {
            const innerHTML = this.getSectionInnerHTML(sectionHTML, section.selector);

            if (innerHTML) {
              target.innerHTML = innerHTML;
            }
          }
        });
      })
      .catch(error => {
        console.error('[MiniCart] Failed to refresh cart sections:', error);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'mini-cart',
        section: 'mini-cart',
        selector: '.shopify-section'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'mobile-cart-icon-bubble',
        section: 'mobile-cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return '';

    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const selectedElement = parsedHTML.querySelector(selector);

    if (!selectedElement) {
      return '';
    }

    return selectedElement.innerHTML;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('mini-cart')) {
  customElements.define('mini-cart', MiniCart);
}